import { PostModelOutputsRequest } from 'clarifai-web-grpc/proto/clarifai/api/service_pb';
import { FilePreview } from 'modules/Playground/Components/ChatInput';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { getModelPredictRequestBody } from 'modules/Models/components/ModelForm/utils/utils';
import { V2 } from 'types/swagger/clarifai-api';
import { JavaScriptValue } from 'google-protobuf/google/protobuf/struct_pb';
import {
  Data,
  Input,
  Image,
  Part,
  Text,
  Deployment,
  Model,
  ModelVersion,
  OutputInfo,
  RunnerSelector,
  Struct,
  UserAppIDSet,
} from 'api/grpc/resources';
import { getMessageHistory } from 'modules/Playground/store/messageStore';
import { convertJsInputToGrpcInput } from 'utils/grpc/jsToGrpc';
import { createInput } from 'utils/inputHelpers';
import { getIsHistorySupported } from './getIsHistorySupported';
import { cleanEmptyFieldsInFieldMap } from './cleanEmptyFieldsInFieldMap';

interface ConstructModelLegacyReqArgs {
  values: PlaygroundConfig;
  text: string;
  images: FilePreview[];
  currentUserId: string;
  userOrOrgId: string;
  appId: string;
}

export const constructModelLegacyReq = ({
  values,
  text,
  images,
  currentUserId,
  userOrOrgId,
  appId,
}: ConstructModelLegacyReqArgs): PostModelOutputsRequest => {
  const fieldValueMap = cleanEmptyFieldsInFieldMap(values.fieldValueMap ?? {});
  const requestBody = getModelPredictRequestBody(fieldValueMap) as V2.V2PostModelOutputs.RequestBody;
  const params = (requestBody?.model?.model_version?.output_info?.params ?? {}) as Record<string, JavaScriptValue>;

  const request = new PostModelOutputsRequest();

  const messageHistory = getMessageHistory();

  const grpcInputParts: Part[] = messageHistory.map((each) => {
    const part = new Part();
    const data = new Data();
    const textGrpc = new Text();
    textGrpc.setRaw(
      JSON.stringify({
        role: each.role,
        content: each.content,
      }),
    );
    data.setText(textGrpc);
    if (each.file?.[0]?.base64) {
      const image = new Image();
      image.setBase64(each.file[0].base64);
      data.setImage(image);
    }
    part.setData(data);
    return part;
  });

  if (getIsHistorySupported(values.model?.id ?? '')) {
    const userAppIdSet = new UserAppIDSet();
    userAppIdSet.setUserId(values.model?.user_id ?? userOrOrgId);
    userAppIdSet.setAppId(values.model?.app_id ?? appId);
    request.setUserAppId(userAppIdSet);

    request.setModelId(values.model?.id ?? '');

    // model object
    const model = new Model();
    model.setId(values.model?.id ?? '');

    // model version
    const modelVersion = new ModelVersion();
    modelVersion.setId(values.modelVersion?.id ?? values.model?.model_version?.id ?? '');
    model.setModelVersion(modelVersion);

    // output params
    const grpcOutputInfo = new OutputInfo();
    grpcOutputInfo.setParams(
      Struct.fromJavaScript({
        ...params,
        chat_history: true,
      }),
    );
    modelVersion.setOutputInfo(grpcOutputInfo);

    // Runner selector
    const grpcRunner = new RunnerSelector();
    const grpcDeployment = new Deployment();
    grpcDeployment.setId(values.deploymentId ?? '');
    grpcDeployment.setUserId(currentUserId); // 👈 Important this needs to be current user, not userOrOrgId
    grpcRunner.setDeployment(grpcDeployment);
    if (values.deploymentId) {
      request.setRunnerSelector(grpcRunner);
    }

    // Inputs
    const inputData = new Data();
    inputData.setPartsList(grpcInputParts);
    const input = new Input();
    input.setData(inputData);
    request.setInputsList([input]);

    request.setModel(model);
  } else {
    const grpcInputs = [
      createInput({
        text: {
          raw: text,
        },
        image: {
          base64: images[0]?.base64,
        },
      }),
    ].map((each) => convertJsInputToGrpcInput(each));
    const userAppIdSet = new UserAppIDSet();
    userAppIdSet.setUserId(values.model?.user_id ?? userOrOrgId);
    userAppIdSet.setAppId(values.model?.app_id ?? appId);
    request.setUserAppId(userAppIdSet);

    request.setModelId(values.model?.id ?? '');

    // model object
    const model = new Model();
    model.setId(values.model?.id ?? '');

    // model version
    const modelVersion = new ModelVersion();
    modelVersion.setId(values.modelVersion?.id ?? values.model?.model_version?.id ?? '');
    model.setModelVersion(modelVersion);

    // output params
    const grpcOutputInfo = new OutputInfo();
    grpcOutputInfo.setParams(Struct.fromJavaScript(params));
    modelVersion.setOutputInfo(grpcOutputInfo);

    // Runner selector
    const grpcRunner = new RunnerSelector();
    const grpcDeployment = new Deployment();
    grpcDeployment.setId(values.deploymentId ?? '');
    grpcDeployment.setUserId(currentUserId); // 👈 Important this needs to be current user, not userOrOrgId
    grpcRunner.setDeployment(grpcDeployment);
    if (values.deploymentId) {
      request.setRunnerSelector(grpcRunner);
    }

    // Inputs
    request.setInputsList(grpcInputs);

    request.setModel(model);
  }

  return request;
};
